import { RefObject, useEffect, useRef } from 'react';

const MESSAGE_TYPE = 'geopard_plan_select_resize';
const ALLOWED_ORIGINS = ['https://geopard.tech'];
const RETRY_DELAYS_MS = [100, 300, 700, 1000, 2000, 3000, 5000, 7000];

/**
 * Hook to handle iframe resizing by communicating the target element's height to the parent window.
 */
export default function useIframeResize(
  targetRef: RefObject<HTMLElement | null>,
) {
  const withinIframe = window !== window.parent;
  const previousHeight = useRef<number>(0);
  const retryTimeoutIdsRef = useRef<number[]>([]);

  useEffect(() => {
    if (!withinIframe || !targetRef.current) {
      return undefined;
    }

    function sendHeight(options?: { schedule?: boolean }) {
      const currentHeight = targetRef.current?.offsetHeight;

      if (currentHeight != null && currentHeight !== previousHeight.current) {
        previousHeight.current = currentHeight;

        ALLOWED_ORIGINS.forEach((origin) => {
          window.parent.postMessage(
            {
              type: MESSAGE_TYPE,
              height: currentHeight,
            },
            origin,
          );
        });
      }

      if (options?.schedule) {
        // Clear previous retries and schedule new ones
        retryTimeoutIdsRef.current.forEach((id) => window.clearTimeout(id));
        retryTimeoutIdsRef.current = RETRY_DELAYS_MS.map((delay) =>
          window.setTimeout(() => sendHeight({ schedule: false }), delay),
        );
      }
    }

    sendHeight({ schedule: true });

    // Schedule retry sends to mitigate parent listener races
    const observer = new ResizeObserver(() => sendHeight({ schedule: true }));
    observer.observe(targetRef.current);

    return () => {
      observer.disconnect();
      retryTimeoutIdsRef.current.forEach((id) => window.clearTimeout(id));
      retryTimeoutIdsRef.current = [];
    };
  }, [withinIframe, targetRef]);
}
